import type { PatternBased, RuleBased, RuleSet } from '@kkp-fraud/shared';
import { t } from './i18n';

/**
 * What changed between the document the engine is running and the draft on screen.
 * The Review & save dialog shows this verbatim — it is the change request the Head of Fraud
 * signs off (§6.5), so it has to be field-level and readable, not a JSON blob.
 */

export type RuleKind = 'ruleBased' | 'patternBased';

export interface FieldChange {
  /** Dotted path inside the rule, or inside the rule set for meta changes. */
  path: string;
  before: unknown;
  after: unknown;
}

export interface RuleDiff {
  id: string;
  name: string;
  kind: RuleKind;
  status: 'added' | 'removed' | 'changed';
  changes: FieldChange[];
}

export interface RuleSetDiff {
  meta: FieldChange[];
  rules: RuleDiff[];
  counts: { added: number; removed: number; changed: number };
  dirty: boolean;
}

function stable(v: unknown): string {
  return JSON.stringify(v ?? null);
}

function same(a: unknown, b: unknown): boolean {
  return stable(a) === stable(b);
}

/**
 * A rule's `points` is not a change anyone signs off: there is no scoring (product owner,
 * 30 Sep 2026), so a points-only difference is no difference. Same as the BFF's
 * `RuleSetDiff` (dotnet/admin-api/Rules/RuleSetDiff.cs).
 *
 * `thresholdConfirmed` likewise: the document keeps it (the engine requires it) but the
 * console no longer shows or changes it (product owner, 30 Sep 2026), so it is never a line in
 * a diff. The BFF's differ, used only by the proposal flow the console no longer renders, is
 * unchanged.
 */
const IGNORED_RULE_KEYS = new Set<string>(['points', 'thresholdConfirmed']);

/** Union of both objects' keys, in a stable order: before's order, then before's new ones. */
function keysOf(a: Record<string, unknown>, b: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const k of Object.keys(a)) if (!out.includes(k)) out.push(k);
  for (const k of Object.keys(b)) if (!out.includes(k)) out.push(k);
  return out.filter((k) => !IGNORED_RULE_KEYS.has(k));
}

function ruleChanges(before: Record<string, unknown>, after: Record<string, unknown>): FieldChange[] {
  const changes: FieldChange[] = [];
  for (const key of keysOf(before, after)) {
    if (!same(before[key], after[key])) {
      changes.push({ path: key, before: before[key], after: after[key] });
    }
  }
  return changes;
}

type AnyRule = RuleBased | PatternBased;

function indexById(rules: readonly AnyRule[] | undefined): Map<string, AnyRule> {
  const map = new Map<string, AnyRule>();
  for (const r of rules ?? []) map.set(r.id, r);
  return map;
}

function diffFamily(kind: RuleKind, before: readonly AnyRule[] | undefined, after: readonly AnyRule[] | undefined): RuleDiff[] {
  const a = indexById(before);
  const b = indexById(after);
  const out: RuleDiff[] = [];

  for (const [id, rule] of b) {
    const prev = a.get(id);
    if (!prev) {
      out.push({ id, name: rule.name, kind, status: 'added', changes: [] });
      continue;
    }
    const changes = ruleChanges(prev as unknown as Record<string, unknown>, rule as unknown as Record<string, unknown>);
    if (changes.length > 0) out.push({ id, name: rule.name, kind, status: 'changed', changes });
  }
  for (const [id, rule] of a) {
    if (!b.has(id)) out.push({ id, name: rule.name, kind, status: 'removed', changes: [] });
  }

  return out.sort((x, y) => x.id.localeCompare(y.id));
}

/**
 * Rule-set level fields: fail-safe policy, the default action, the document note, the feeder
 * payload configuration and the alert configuration (one setting each, however many rows
 * changed). The document's `thresholds` (the old score bands) are not listed: they take no
 * part in any decision since 30 Sep 2026.
 */
const META_PATHS: { path: string; get: (rs: RuleSet) => unknown }[] = [
  { path: 'failSafe.cardPresentAction', get: (rs) => rs.failSafe?.cardPresentAction },
  { path: 'failSafe.ecommerceAction', get: (rs) => rs.failSafe?.ecommerceAction },
  { path: 'failSafe.ecommerceMinAmountThb', get: (rs) => rs.failSafe?.ecommerceMinAmountThb },
  { path: 'note', get: (rs) => rs.note },
  { path: 'defaultAction', get: (rs) => rs.defaultAction },
  { path: 'feederFields', get: (rs) => rs.feederFields },
  { path: 'alerts', get: (rs) => rs.alerts },
  // 2c rule sets (a): the set-level rules, one setting however many changed.
  { path: 'setRules', get: (rs) => rs.setRules },
  // Wave 6: the custom field bindings, one setting however many changed.
  { path: 'customFields', get: (rs) => rs.customFields },
];

export function computeDiff(loaded: RuleSet | null, draft: RuleSet | null): RuleSetDiff {
  const empty: RuleSetDiff = { meta: [], rules: [], counts: { added: 0, removed: 0, changed: 0 }, dirty: false };
  if (!loaded || !draft) return empty;

  const meta: FieldChange[] = [];
  for (const { path, get } of META_PATHS) {
    const before = get(loaded);
    const after = get(draft);
    if (!same(before, after)) meta.push({ path, before, after });
  }

  const rules = [
    ...diffFamily('ruleBased', loaded.ruleBased, draft.ruleBased),
    ...diffFamily('patternBased', loaded.patternBased, draft.patternBased),
  ];

  const counts = {
    added: rules.filter((r) => r.status === 'added').length,
    removed: rules.filter((r) => r.status === 'removed').length,
    changed: rules.filter((r) => r.status === 'changed').length,
  };

  return { meta, rules, counts, dirty: meta.length > 0 || rules.length > 0 };
}

/** "3 changed · 1 added" — the header summary. */
export function summariseDiff(diff: RuleSetDiff): string {
  if (!diff.dirty) return t('diff.summary.none');
  const bits: string[] = [];
  if (diff.counts.added) bits.push(t('diff.summary.added', { n: diff.counts.added }));
  if (diff.counts.removed) bits.push(t('diff.summary.removed', { n: diff.counts.removed }));
  if (diff.counts.changed) bits.push(t('diff.summary.changed', { n: diff.counts.changed }));
  if (diff.meta.length) bits.push(t(diff.meta.length === 1 ? 'diff.summary.setting.one' : 'diff.summary.setting.other', { n: diff.meta.length }));
  return bits.join(' · ');
}

/** A value as it appears in the diff table. */
export function formatDiffValue(v: unknown): string {
  if (v === undefined) return t('diff.value.unset');
  if (v === null) return 'null';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}
